<template>
    <div class="task-obj-value text-icon">
        <template v-if="!isEdit">
            <SelectSearch
                ref="search"
                class="mr-1"
                name="task-obj-value"
                :placeholder="inputText.length > 0 ? inputText : 'Введите название объекта'"
                :labelCallback="item => item.id ? item.name : 'Введите название объекта'"
                empty=""
                :items="items"
                :searchText="inputText"
                :active="[activeId]"
                :un-focus-clear-value="false"
                @blur="createObject"
                @changeSearchText="val => inputText = val.trim()"
                @selectChange="selectItem"
            />

            <template v-if="activeId && !($refs.search || {}).isShowInput">
                <TipMessage text="Изменить выбранный объект">
                    <Icon id="edit" size="18" color="green" @click="startEditObject"/>
                </TipMessage>
                <TipMessage text="Удалить выбранный объект">
                    <Icon id="delete" size="18" color="purple" @click="deleteRequest"/>
                </TipMessage>
            </template>
        </template>
        <template v-else-if="activeId">
            <div class="placeholder placeholder_input placeholder_icon">
                <input
                    type="text"
                    v-focus
                    v-model.trim="inputText"
                    @blur="finishEditObject"
                >
                <Icon id="edit"/>
            </div>
            <Icon id="delete" size="18" @click="stopEditObject" color="purple"/>
        </template>
    </div>
</template>

<script>
import {mapState} from "vuex";
import SelectSearch from "@common/vue/components/form/SelectSearch";

/**
 * Этот компонент позволяет:
 * - Найти объект/бренд из списка (компонент SelectSearch и свойство inputText)
 * - Выбрать существующий объект
 * - Выбрать объект из бренда (автоматически создать объект и выбрать)
 * - Создать новый объект
 * - Редактировать выбранный объект
 * - Удалить выбранный объект (в списке останется одноименный бренд если существует)
 */
export default {
    name: "TaskObject",
    components: {SelectSearch},
    props: ['activeId'],
    computed: {
        ...mapState({
            objects: state => state.planObjects.descriptionObjects,
            brands: state => state.planObjects.brands,
            urlId: state => state.planCRUD.urlId,
            runId: state => state.planCRUD.runId,
            service: state => state.planCRUD.service,
        }),

        /**
         * Бренды объединенные с объектами по имени (объекты в приоритете)
         * @returns {Array}
         */
        items() {
            const result = [{id: '', name: 'Не выбрано'}, ...this.objects];
            const objectNames = this.objects.map(o => o.name);
            this.brands.forEach((brand) => {
                // No exist.
                if (!objectNames.includes(brand.name)) {
                    result.push(brand);
                }
            });

            return result;
        },

        /**
         * Выбранный элемент по ID
         * @returns {Object|null}
         */
        active() {
            return this.objects.find(obj => obj.id === this.activeId) || null;
        },
    },
    data() {
        return {
            /** Режим редактирования выбранного объекта */
            isEdit: false,

            /** Текст для поиска из списка брендов/объектов */
            inputText: '',
        };
    },
    mounted() {
        this.$store.dispatch('fetchDescriptionObjects', {
          service: this.service,
          runId: this.runId,
          urlId: this.urlId,
        })
        this.$store.dispatch('fetchBrands', {
          service: this.service,
          runId: this.runId,
          urlId: this.urlId,
        });
    },
    methods: {
        selectItem(selectedId) {
            selectedId = String(selectedId);

            // Show another variants.
            this.inputText = '';

            const object = this.objects.find(o => o.id === selectedId);
            if (object) return this.selectObject(object);

            const brand = this.brands.find(b => b.id === selectedId);
            if (brand) return this.selectBrand(brand);

            this.resetActive();
        },

        /** Create new object (when focus is blur). */
        createObject({event, component}) {
            const objectName = String(this.inputText).trim();

            if (!objectName) return false;

            this.inputText = '';
            this.$refs.search.isShowInput = false;

            // Check when object exists.
            const object = this.objects.find(s => s.name === objectName);
            if (object) return this.setActive(object.id);

            this._createObject(objectName)
                .then(r => this.setActive(r.data.object.id));
        },

        /** Edit object. */
        startEditObject() {
            this.inputText = this.active.name;
            this.isEdit = true;
        },
        stopEditObject() {
            this.isEdit = false;
            this.inputText = '';
        },
        finishEditObject() {
            if (this.inputText) {
                const object = this.objects.find(s => s.name === this.inputText);
                // You cannot create two identical names objects.
                if (!object) {
                    // Rename to active input text for active object.
                    return this._updateObject(this.activeId, this.inputText)
                        .then(() => this.stopEditObject());

                } else {
                    const err = {title: 'Ошибка регистрации объекта'};
                    this.$dialog.showErrors(
                        err,
                        `Объект с именем ${object.name} уже существует, выберите его или удалите для переименования.`
                    );
                }
            }

            this.isEdit = false;
        },

        /**
         * Ask confirm delete object question.
         */
        deleteRequest(e) {
            this.$dialog.showConfirm({
                    body: `Действительно удалить "${this.active.name}"?`,
                    confirmText: "Удалить",
                }, this.deleteActive);
        },

        /** Utils. */

        /** Select object */
        selectObject(object) {
            this.setActive(object.id);
            this.$refs.search.isShowInput = false;
        },

        /** Select brand (create and select object) */
        selectBrand(brand) {
            this._createObject(brand.name)
                .then(r => this.setActive(r.data.object.id));
            this.$refs.search.isShowInput = false;
        },

        setActive(id) {
            this.$emit('setActive', id);
        },

        resetActive() {
            this.$emit('setActive', '');
        },

        deleteActive() {
            this._deleteObject(this.activeId)
                .then(r => this.resetActive())
        },

        _createObject(name) {
            return this.$store.dispatch('createDescriptionObject', {name})
                .catch(e => {
                    const err = {title: 'Ошибка регистрации объекта'};
                    this.$dialog.showErrors(err, `Не удалось зарегистрировать объект "${name}". Попробуйте позже.`);

                    throw new Error(err.title);
                });
        },

        _updateObject(id, name) {
            return this.$store.dispatch('updateDescriptionObject', {id, name})
                .then(response => {
                    if ("success" === response.data.status) return response;
                    throw new Error();
                })
                .catch(e => {
                    const err = {title: 'Ошибка обновления объекта'};
                    this.$dialog.showErrors(err, 'Не удалось обновить объект. Попробуйте позже.');

                    throw new Error(err.title);
                });
        },

        _deleteObject(id) {
            return this.$store.dispatch('deleteDescriptionObject', {id})
                .catch(e => {
                    const err = {title: 'Ошибка удаления объекта'};
                    this.$dialog.showErrors(err, 'Не удалось удалить объект. Попробуйте позже.');

                    throw new Error(err.title);
                });
        },
    }
}
</script>
